import cronstrue from 'cronstrue';
import {
  JobDetailsDto,
  JobFormData,
  JobOverview,
  JobStatus,
  Repetition,
} from './models/job';
import { PartType } from './models/filename';
import { ExportTargetDto } from './models/export';

export const HTTP_CODES = {
  BAD_REQUEST: 400,
  OK: 200,
  CREATED: 201,
  ACCEPTED: 202,
  NO_CONTENT: 204,
  UNAUTHORIZED: 403,
};

export type FetchStatus = 'idle' | 'loading' | 'success' | 'error';

export const repetitionToString = (repetition: Repetition): string => {
  return repetition.type === 'afterMorningBatch'
    ? 'Executed after the morning batch has been processed'
    : repetition.type === 'afterNightBatch'
      ? 'Executed after the morning batch has been processed'
      : `${
          repetition.type.charAt(0).toUpperCase() + repetition.type.slice(1)
        } ${cronstrue.toString(repetition.cronValue!)}`;
};

export const sortSchedulesByStatus = (
  a: JobOverview,
  b: JobOverview
): number => {
  const aValue = statusOrder(a.status);
  const bValue = statusOrder(b.status);
  return aValue - bValue;
};

export const statusOrder = (status: string): number => {
  if (status === JobStatus.Failed) {
    return 0;
  } else if (status === JobStatus.WithWarnings) {
    return 1;
  } else if (status === JobStatus.Running) {
    return 2;
  } else if (status === JobStatus.Scheduled) {
    return 3;
  }
  return 4;
};

export const repetitionTypeOrder = (repetition: Repetition): number => {
  switch (repetition.type) {
    case 'personalized':
      return 1;
    case 'hourly':
      return 2;
    case 'daily':
      return 3;
    case 'weekly':
      return 4;
    case 'monthly':
      return 5;
  }
  return -1;
};

export const containsAll = <T>(arr1: Array<T>, arr2: Array<T>) =>
  arr2.every(arr2Item => arr1.includes(arr2Item));

export const sameMembers = <T>(arr1: Array<T>, arr2: Array<T>) =>
  containsAll(arr1, arr2) && containsAll(arr2, arr1);

export const range = (start: number, end: number) => {
  const length = end - start + 1;
  return Array.from({ length }, (_, idx) => idx + start);
};

export const mapJobDetailsToJobFormData = (
  jobDetails: JobDetailsDto
): JobFormData => {
  const exportTarget: ExportTargetDto | null =
    jobDetails.exportTargetSettings?.url
      ? {
        url: jobDetails.exportTargetSettings.url,
        outputType: jobDetails.exportTargetSettings.outputType || 'excel',
        name: jobDetails.exportTargetSettings.name || [
          { partType: PartType.QueryName },
        ],
      }
      : null;

  return {
    id: jobDetails.id,
    description: jobDetails.description,
    title: jobDetails.title,
    activeFrom: jobDetails.activeFrom.split('T')[0],
    activeTo: jobDetails.activeTo.split('T')[0],
    isActive: jobDetails.isActive,
    query: {
      id: jobDetails.query.id,
      name: '',
      parameters: [],
      storesAvailableIn: [],
    },
    repetition: jobDetails.repetition,
    storesIds: jobDetails.stores.map(s => s.id),
    queryParameters: jobDetails.queryParameters.map(p => ({
      ...p,
      value: p.value,
    })),
    exportTargetSettings: exportTarget,
  };
};

export const emitTelemetry = (
  eventName: string,
  payload: Record<string, unknown>
) => {
  if (typeof window === 'undefined') {
    return;
  }

  window.dispatchEvent(
    new CustomEvent('fms.telemetry', {
      detail: {
        eventName,
        payload,
      },
    })
  );
};
